<script setup>
    // Component
    import BaseInput from '@/components/BaseInputComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';

    // Store
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useHeaderStore } from '@/stores/header/headerStore';
    import { useSetupUserStore } from '@/stores/setup/setupUserStore';
    import { useUserStore } from '@/stores/user/userStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // Vue
    import { getCurrentInstance, inject, onMounted, reactive, ref } from 'vue';
    import { useRouter } from 'vue-router';

    // Else
    import i18n from '@/common/i18n';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();

    const loginStore = useLoginStore();

    const headerStore = useHeaderStore();

    const setupUserStore = useSetupUserStore();

    const userStore = useUserStore();

    const date = new Date();
    const year = date.getFullYear();

    const birthdayYear = ref('1980');
    const birthdayMonth = ref('1');
    const birthdayDay = ref('1');

    const setupForm = reactive({
        NameKjLs: '',
        NameKjFs: '',
        NameKnLs: '',
        NameKnFs: '',
        MailAddress: '',
        Birthday: '',
        Gender: '',
        Lang: '',
        PhoneNumber: '',
    });

    const birthdayYearList = ref([]);
    const birthdayMonthList = ref([]);
    const birthdayDayList = ref([]);

    // 画面上の活性・非活性を制御するフラグ。
    const isLoginUserFormInputNotAvailavle = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(async () => {
        // 生年月日のプルダウンの初期を設定する。
        // 初期値は今年。
        // birthdayYear.value = year;

        // ユーザー情報詳細取得APIをコールして、ユーザー情報を取得する。
        const response = await userStore.getUserDetailAction(API_URL, null);
        if (response.success) {
            setupForm.NameKjLs = Utils.changeNullValueToEmpty(userStore.userDetail.NameKjLs);
            setupForm.NameKjFs = Utils.changeNullValueToEmpty(userStore.userDetail.NameKjFs);
            setupForm.NameKnLs = Utils.changeNullValueToEmpty(userStore.userDetail.NameKnLs);
            setupForm.NameKnFs = Utils.changeNullValueToEmpty(userStore.userDetail.NameKnFs);
            setupForm.MailAddress = Utils.changeNullValueToEmpty(userStore.userDetail.MailAddress);
            setupForm.Birthday = Utils.changeNullValueToEmpty(userStore.userDetail.Birthday);
            setupForm.Gender = Utils.changeNullValueToEmpty(userStore.userDetail.Gender);
            setupForm.Lang = Utils.changeNullValueToEmpty(userStore.userDetail.Lang);
            setupForm.PhoneNumber = Utils.changeNullValueToEmpty(userStore.userDetail.PhoneNumber);

            // ユーザー登録対象のユーザーがすでに初期設定されており、登録契機に応じて画面上の設定項目の活性・非活性を制御する。
            if (!Utils.isNullEmpty(userStore.userDetail.RegistMethod)) {
                switch (userStore.userDetail.RegistMethod) {
                    case '1':
                    case '3':
                    case '5':
                        // 管理者登録
                        // 管理者登録(オペレータ)
                        // タフネス連携
                        isLoginUserFormInputNotAvailavle.value = true;
                        break;

                    case '2':
                    case '4':
                    default:
                        // 自己登録
                        // 自己登録(オペレータ)
                        isLoginUserFormInputNotAvailavle.value = false;
                        break;
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }

        // 生年月日の年のプルダウンの選択肢を生成する。
        for (let i = year; i >= 1900; i--) {
            birthdayYearList.value.push(i);
        }

        // 生年月日の月のプルダウンの選択肢を生成する。
        for (let i = 1; i <= 12; i++) {
            birthdayMonthList.value.push(i);
        }

        // 生年月日の日のプルダウンの選択肢を生成する。
        for (let i = 1; i <= 31; i++) {
            birthdayDayList.value.push(i);
        }

        // ユーザーキー番号が設定されている場合のみ、APIのリクエストボディに、パラメーターを付与する。
        if (loginStore.getUserKeyNoSupport) {
            setupForm['UserKeyNo'] = '';
            if (!Utils.isNullEmpty(userStore.userDetail.UserKeyNo)) {
                setupForm['UserKeyNo'] = Utils.changeNullValueToEmpty(userStore.userDetail.UserKeyNo);
            }
        }

        // 生年月日を復元できた場合。
        if (!Utils.isNullEmpty(setupForm.Birthday)) {
            const [y, m, d] = setupForm.Birthday.split('/');

            birthdayYear.value = String(Number(y));
            birthdayMonth.value = String(Number(m));
            birthdayDay.value = String(Number(d));
        }
    });

    /**
     * 数字が10未満の場合は、数字を0埋めする。
     * @param num
     */
    function setZeroToString(num) {
        return num < 10 ? String('0' + num) : String(num);
    }

    /**
     * ログイン後、はじめのユーザーや、未登録のユーザーのユーザー情報登録を行う。
     */
    async function checkForm() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 選択された生年月日を結合して、APIに送信する。
        setupForm.Birthday = birthdayYear.value + '/' + setZeroToString(birthdayMonth.value) + '/' + setZeroToString(birthdayDay.value);

        // APIをコールするためのリクエストボディを生成する。
        let requestBody = {};

        requestBody['NameKjLs'] = setupForm.NameKjLs;
        requestBody['NameKjFs'] = setupForm.NameKjFs;

        // 任意項目である、氏名(カナ)のセイが入力されている場合は、リクエストボディにデータを設定する。
        if (!Utils.isNullEmpty(setupForm.NameKnLs)) {
            requestBody['NameKnLs'] = setupForm.NameKnLs;
        }

        // 任意項目である、氏名(カナ)のメイが入力されている場合は、リクエストボディにデータを設定する。
        if (!Utils.isNullEmpty(setupForm.NameKnFs)) {
            requestBody['NameKnFs'] = setupForm.NameKnFs;
        }

        requestBody['MailAddress'] = setupForm.MailAddress;
        requestBody['Birthday'] = setupForm.Birthday;
        requestBody['Gender'] = setupForm.Gender;
        requestBody['Lang'] = setupForm.Lang;
        requestBody['PhoneNumber'] = setupForm.PhoneNumber;

        // ユーザーキー番号が有効の場合。
        if (loginStore.getUserKeyNoSupport) {
            // 画面に設定されているユーザーキー番号をAPIのリクエストボディに設定する。
            requestBody['UserKeyNo'] = setupForm.UserKeyNo;
        }

        // ユーザー状態設定APIをコールして、ユーザーを登録する。
        const response = await setupUserStore.setupUserDetailAction(API_URL, requestBody);

        if (response.success) {
            // ログイン中ユーザー情報を格納するStore情報を初期化する。
            // 新規サインアップに基づくユーザー情報をAPIから取得し、最新のユーザー情報を格納しなおす。
            loginStore.userId = '';
            loginStore.nameKjLs = '';
            loginStore.nameKjFs = '';
            loginStore.vendorType = '';
            loginStore.userKeyNoSupport = '';
            loginStore.userKeyNoNm = '';
            loginStore.phoneNumber = '';
            loginStore.mailAddress = '';

            // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
            const response2 = await loginStore.getLoginUserInfoAction(API_URL, null);

            if (response2.success) {
                // ユーザー状態確認(ログイン)APIをコールして、ユーザーの各種状態を取得する。
                const response3 = await loginStore.getUserStatusByLoginAction(API_URL, null);

                // 正常応答の場合。
                if (response3.success) {
                    // 有効な契約が存在するか判定する。
                    // 有効化契約が存在しない場合は、エラーメッセージを表示して、画面に留まる。
                    if (response3.responseBody.ContractAssigned) {
                        // ログインしたユーザーのログイン後の画面の切り替えを制御する。
                        if (response3.responseBody.ParentAgreeNeed2Check) {
                            // 親権者同意確認要否を判定する。

                            // 親権者同意画面へ遷移する。
                            router.push({ name: 'setupParent' });
                        } else {
                            // 親権者同意確認は不要、もしくは、すでに同意済み。

                            // ホーム画面へ遷移する。
                            router.push({ name: 'home' });
                        }
                    } else {
                        // 有効な契約が存在しない場合はエラーにする。
                        messageDialog('error', i18n.global.t('common.message.isContractAssignError'));

                        // ログアウト処理を実行する。
                        const logoutUrl = await headerStore.logoutAction();

                        // ログアウトの遷移先に応じて移動する。
                        if (logoutUrl.href) {
                            location.href = logoutUrl.url;
                        } else {
                            router.push({ name: logoutUrl.url, query: logoutUrl.query });
                        }
                    }
                } else {
                    messageDialog('error', response3.errorMessage);
                    if (response3.redirect) {
                        location.href = response3.redirectUrl;
                    }
                    return;
                }
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
                return;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 氏名(漢字)の姓の入力チェック。
        if (Utils.isNullEmpty(setupForm.NameKjLs)) {
            message += i18n.global.t('setupUser.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfShortString(setupForm.NameKjLs)) {
                message += i18n.global.t('setupUser.message.message2') + '\n';
            }
        }

        // 氏名(漢字)の名の入力チェック。
        if (Utils.isNullEmpty(setupForm.NameKjFs)) {
            message += i18n.global.t('setupUser.message.message3') + '\n';
        } else {
            if (Utils.checkLengthOfShortString(setupForm.NameKjFs)) {
                message += i18n.global.t('setupUser.message.message4') + '\n';
            }
        }

        //  任意項目である、氏名(カナ)のセイの入力チェック。
        if (!Utils.isNullEmpty(setupForm.NameKnLs)) {
            if (Utils.checkLengthOfString(setupForm.NameKnLs)) {
                message += i18n.global.t('setupUser.message.message6') + '\n';
            }
            if (!Utils.isMatchPatternKana(setupForm.NameKnLs)) {
                message += i18n.global.t('setupUser.message.message7') + '\n';
            }
        }

        //  任意項目である、氏名(カナ)のメイの入力チェック。
        if (!Utils.isNullEmpty(setupForm.NameKnFs)) {
            if (Utils.checkLengthOfString(setupForm.NameKnFs)) {
                message += i18n.global.t('setupUser.message.message9') + '\n';
            }
            if (!Utils.isMatchPatternKana(setupForm.NameKnFs)) {
                message += i18n.global.t('setupUser.message.message10') + '\n';
            }
        }

        // メールアドレスの入力チェック。
        if (Utils.isNullEmpty(setupForm.MailAddress)) {
            message += i18n.global.t('setupUser.message.message11') + '\n';
        } else {
            if (Utils.checkLengthOfMailAddress(setupForm.MailAddress)) {
                message += i18n.global.t('setupUser.message.message12') + '\n';
            }
            if (!Utils.isMatchPatternMailAddress(setupForm.MailAddress)) {
                message += i18n.global.t('setupUser.message.message13') + '\n';
            }
        }

        // 生年月日の年の選択チェック。
        if (Utils.isNullEmpty(birthdayYear.value)) {
            message += i18n.global.t('setupUser.message.message14') + '\n';
        }

        // 生年月日の月の選択チェック。
        if (Utils.isNullEmpty(birthdayMonth.value)) {
            message += i18n.global.t('setupUser.message.message15') + '\n';
        }

        // 生年月日の日の選択チェック。
        if (Utils.isNullEmpty(birthdayDay.value)) {
            message += i18n.global.t('setupUser.message.message15') + '\n';
        }

        // 有効な日付の組み合わせが選択されているかチェックする。
        if (!Utils.isNullEmpty(birthdayYear.value) && !Utils.isNullEmpty(birthdayMonth.value) && !Utils.isNullEmpty(birthdayDay.value)) {
            // 日付の妥当性をチェック
            const selectDate = new Date(Number(birthdayYear.value), Number(birthdayMonth.value) - 1, Number(birthdayDay.value));
            const yearDate = selectDate.getFullYear();
            const monthDate = selectDate.getMonth() + 1;
            const dayDate = selectDate.getDate();

            const selectDate1 = yearDate + '/' + String(monthDate).padStart(2, '0') + '/' + String(dayDate).padStart(2, '0');
            const selectDate2 = birthdayYear.value + '/' + setZeroToString(birthdayMonth.value) + '/' + setZeroToString(birthdayDay.value);
            if (selectDate1 !== selectDate2) {
                message += i18n.global.t('common.message.isNotDateAvailavleError') + '\n';
            }

            // 生年月日が未来日かチェック
            let todaySet = new Date();
            let YYYY = todaySet.getFullYear();
            let MM = ('00' + (todaySet.getMonth() + 1)).slice(-2);
            let DD = ('00' + todaySet.getDate()).slice(-2);
            let today = YYYY + '/' + MM + '/' + DD;
            if (today < selectDate1) {
                message += i18n.global.t('common.message.isNotDateAvailavleError') + '\n';
            }
        }

        // 性別の選択チェック。
        if (Utils.isNullEmpty(setupForm.Gender)) {
            message += i18n.global.t('setupUser.message.message17') + '\n';
        }

        // 言語の選択チェック。
        if (Utils.isNullEmpty(setupForm.Lang)) {
            message += i18n.global.t('setupUser.message.message18') + '\n';
        }

        // 電話番号の入力チェック。
        if (Utils.isNullEmpty(setupForm.PhoneNumber)) {
            message += i18n.global.t('setupUser.message.message19') + '\n';
        } else {
            if (Utils.checkLengthOfTelNumber(setupForm.PhoneNumber)) {
                message += i18n.global.t('setupUser.message.message20') + '\n';
            }
            if (!Utils.isMatchPatternTelNumber(setupForm.PhoneNumber)) {
                message += i18n.global.t('setupUser.message.message21') + '\n';
            }
        }

        // ユーザー独自のユーザーキー番号が設定されている場合のみ、ユーザーキー番号のチェックを行う。
        if (loginStore.getUserKeyNoSupport) {
            if (Utils.isNullEmpty(setupForm.UserKeyNo)) {
                message += loginStore.getUserKeyNoNm + i18n.global.t('setupUser.message.message22') + '\n';
            } else {
                if (Utils.checkLengthOfShortString(setupForm.UserKeyNo)) {
                    message += loginStore.getUserKeyNoNm + i18n.global.t('setupUser.message.message23') + '\n';
                }
                if (!Utils.isMatchPatternAlphabetNumber(setupForm.UserKeyNo)) {
                    message += loginStore.getUserKeyNoNm + i18n.global.t('setupUser.message.message24') + '\n';
                }
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    function cancel() {
        // ログイン画面へ遷移する。
        router.push({ name: 'login', query: { vid: loginStore.getVendorId } });
    }
</script>

<template>
    <section id="main" class="bg">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <span>{{ $t('setupUser.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <section class="form">
                        <dl>
                            <!--氏名（漢字）-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl1') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput name">
                                    <li>
                                        <BaseInput v-model:input="setupForm.NameKjLs" type="text" :placeholder="$t('setupUser.parts.placeholder1')" :disabled="isLoginUserFormInputNotAvailavle" />
                                    </li>
                                    <li>
                                        <BaseInput v-model:input="setupForm.NameKjFs" type="text" :placeholder="$t('setupUser.parts.placeholder2')" :disabled="isLoginUserFormInputNotAvailavle" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--氏名（カナ）-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl2') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput name">
                                    <li>
                                        <BaseInput v-model:input="setupForm.NameKnLs" type="text" :placeholder="$t('setupUser.parts.placeholder3')" :disabled="isLoginUserFormInputNotAvailavle" />
                                    </li>
                                    <li>
                                        <BaseInput v-model:input="setupForm.NameKnFs" type="text" :placeholder="$t('setupUser.parts.placeholder4')" :disabled="isLoginUserFormInputNotAvailavle" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--メールアドレス-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl3') }}</span>
                                    </li>
                                    <li class="must" style="margin-right: 10px">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.MailAddress" type="email" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--生年月日-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl4') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput birthday">
                                    <li>
                                        <div class="select">
                                            <select v-model="birthdayYear" required>
                                                <option v-for="(birYear, index) in birthdayYearList" :key="index" :value="birYear">{{ birYear }}</option>
                                            </select>
                                        </div>
                                    </li>
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl4Label1') }}</span>
                                    </li>
                                    <li>
                                        <div class="select">
                                            <select v-model="birthdayMonth" required>
                                                <option v-for="(month, index) in birthdayMonthList" :key="index" :value="month">{{ month }}</option>
                                            </select>
                                        </div>
                                    </li>
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl4Label2') }}</span>
                                    </li>
                                    <li>
                                        <div class="select">
                                            <select v-model="birthdayDay" required>
                                                <option v-for="(day, index) in birthdayDayList" :key="index" :value="day">{{ day }}</option>
                                            </select>
                                        </div>
                                    </li>
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl4Label3') }}</span>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--性別-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl5') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <div class="select">
                                            <select v-model="setupForm.Gender" required>
                                                <option value="">{{ $t('common.parts.selectOptionDefault') }}</option>
                                                <option value="1">{{ $t('setupUser.parts.selectOption1') }}</option>
                                                <option value="2">{{ $t('setupUser.parts.selectOption2') }}</option>
                                                <option value="3">{{ $t('setupUser.parts.selectOption3') }}</option>
                                            </select>
                                        </div>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--言語-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl6') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <div class="select">
                                            <select v-model="setupForm.Lang" required>
                                                <option value="">{{ $t('common.parts.selectOptionDefault') }}</option>
                                                <option value="1">{{ $t('setupUser.parts.selectOption4') }}</option>
                                                <option value="2">{{ $t('setupUser.parts.selectOption5') }}</option>
                                            </select>
                                        </div>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--電話番号-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('setupUser.parts.dl7') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                    <li>
                                        <Tooltip>
                                            {{ $t('setupUser.parts.blowoutInformation1') }}
                                        </Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.PhoneNumber" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl v-if="loginStore.getUserKeyNoSupport">
                            <dt>
                                <!--ユーザーキー番号-->
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ loginStore.getUserKeyNoNm }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="setupForm.UserKeyNo" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--キャンセル-->
                            <button type="button" class="btnNormal" @click="cancel">
                                {{ $t('common.parts.cancel') }}
                            </button>
                        </li>
                        <li>
                            <!--登録-->
                            <button type="button" class="btnPrimary" @click="checkForm" :disabled="disabledStore.isDisabled">
                                {{ $t('common.parts.entry') }}
                            </button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
